import clsx from 'clsx';
import { useMemo } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { getCSSVariableValue } from '../../../../_metronic/assets/ts/_utils';

interface Props {
  name: string;
  picture?: string;
  tooltipLabel?: React.ReactNode;
  size?: '20px' | '25px' | '30px' | '35px' | '40px' | '45px' | '50px' | '55px' | '60px';
  labelFontSize?: 'fs-1' | 'fs-2' | 'fs-3' | 'fs-4' | 'fs-5' | 'fs-6' | 'fs-7' | 'fs-8' | 'fs-9';
  className?: string;
  disableNameTooltip?: boolean;
}

export default function Avatar({
  name,
  picture,
  tooltipLabel,
  size = '25px',
  labelFontSize = 'fs-8',
  className,
  disableNameTooltip = false,
}: Props) {
  const colors: {
    color: string;
    fontColor: 'light' | 'dark';
  }[] = [
    { color: getCSSVariableValue('--bs-primary'), fontColor: 'light' },
    { color: getCSSVariableValue('--bs-success'), fontColor: 'light' },
    { color: getCSSVariableValue('--bs-warning'), fontColor: 'light' },
    { color: getCSSVariableValue('--bs-danger'), fontColor: 'light' },
    { color: getCSSVariableValue('--bs-info'), fontColor: 'light' },
    { color: getCSSVariableValue('--bs-orange'), fontColor: 'light' },
    { color: getCSSVariableValue('--bs-cyan'), fontColor: 'dark' },
    { color: getCSSVariableValue('--bs-pink'), fontColor: 'light' },
    { color: getCSSVariableValue('--bs-gray'), fontColor: 'light' },
    { color: getCSSVariableValue('--bs-green'), fontColor: 'light' },
    { color: getCSSVariableValue('--bs-purple'), fontColor: 'light' },
    { color: getCSSVariableValue('--bs-red'), fontColor: 'light' },
    { color: getCSSVariableValue('--bs-blue'), fontColor: 'light' },
    { color: getCSSVariableValue('--bs-teal'), fontColor: 'light' },
    { color: getCSSVariableValue('--bs-secondary'), fontColor: 'dark' },
  ];

  const initials = useMemo(() => {
    if (picture) return ''; // skip if has picture

    const nameParts = name.trim().split(/\s+/);
    if (nameParts.length === 1) {
      return nameParts[0].charAt(0).toUpperCase();
    }
    return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
  }, [name]);

  // Get background color based on name
  const backgroundColor = useMemo(() => {
    if (picture) return colors[0]; // skip if has picture

    // DJB2 hash algorithm for good distribution
    let hash = 5381;
    for (let i = 0; i < name.length; i++) {
      hash = (hash << 5) + hash + name.charCodeAt(i);
    }

    const index = Math.abs(hash) % colors.length;
    return colors[index];
  }, [name]);

  const renderAvatar = () => (
    <div className={clsx(`symbol symbol-circle symbol-${size} border-0`, className)}>
      {picture ? (
        <img alt={name} src={picture} />
      ) : (
        <div
          className={clsx(
            `symbol-label ${labelFontSize} fw-bold text-${backgroundColor.fontColor}`,
            className
          )}
          style={{
            backgroundColor: backgroundColor.color,
          }}
        >
          {initials}
        </div>
      )}
    </div>
  );

  return (
    <div className='symbol-group symbol-hover flex-nowrap'>
      {disableNameTooltip ? (
        renderAvatar()
      ) : (
        <OverlayTrigger placement='bottom' overlay={<Tooltip>{tooltipLabel || name}</Tooltip>}>
          {renderAvatar()}
        </OverlayTrigger>
      )}
    </div>
  );
}
